// pdf generation
import * as pdfjsLib from "pdfjs-dist";
import pdfWorker from "pdfjs-dist/build/pdf.worker.entry";
import { parsePhoneNumberFromString } from "libphonenumber-js";

pdfjsLib.GlobalWorkerOptions.workerSrc = pdfWorker;


export const validateRequiredFields = async (queryParams, templateConfig) => {
  const errors = {};

  const template = queryParams.template;
  const templateConfigData = templateConfig.templates?.[template] || {};
  const requiredFields = templateConfigData.required_input_fields || [];
  const requiredFieldLabels = templateConfigData.required_field_labels || {};

  requiredFields.forEach((field) => {
    let fieldFound = false;

    if (field === "template_name") {
      return;
    }
    for (const section in queryParams) {
      if (queryParams[section]?.[field] !== undefined) {
        fieldFound = true;
        if (!queryParams[section][field].trim()) {
          const label = requiredFieldLabels[field] || field;
          errors[field] = `${label} is required`;
        }
        break;
      }
    }
    if (!fieldFound) {
      const label = requiredFieldLabels[field] || field;
      errors[field] = `${label} is required`;
    }
  });

  return errors;
};

export const buildQueryParams = (selectedTemplate, formData) => {
  const templateData = formData[selectedTemplate];
  const queryParams = {
    template: selectedTemplate,
    prospect: templateData.prospectsQueryParams,
    customer: templateData.customerQueryParams,
  };
  if (selectedTemplate.includes("Agent Letter")) {
    queryParams.campaign = templateData.campaignQueryParams;
  }
  return queryParams;
};

export const copyArrayBuffer = (arrayBuffer) => {
  const copiedBuffer = new ArrayBuffer(arrayBuffer.byteLength);
  const view = new Uint8Array(copiedBuffer);
  view.set(new Uint8Array(arrayBuffer));
  return copiedBuffer;
};

export const renderPdf = async (pdfBinaryData) => {
  try {
    const pdfDataCopy = copyArrayBuffer(pdfBinaryData);
    const pdf = await pdfjsLib.getDocument({ data: pdfDataCopy }).promise;

    const pdfImages = [];
    for (let pageNumber = 1; pageNumber <= pdf.numPages; pageNumber++) {
      const page = await pdf.getPage(pageNumber);

      const scale = 1.5;
      const viewport = page.getViewport({ scale });

      const canvas = document.createElement("canvas");
      const context = canvas.getContext("2d");

      canvas.width = viewport.width;
      canvas.height = viewport.height;

      await page.render({ canvasContext: context, viewport }).promise;

      pdfImages.push(canvas.toDataURL());
    }

    return pdfImages;
  } catch (error) {
    throw error;
  }
};


export const constructAndReturnValidationErrorMessage = (
  rawMessage,
  errorMessageString
) => {
  if (!rawMessage || !errorMessageString) {
    throw new Error("Missing required arguments.");
  }

  if (typeof errorMessageString !== "string") {
    throw new TypeError("errorMessageString must be a string");
  }

  let messageList = [];

  try {
    messageList = Array.isArray(rawMessage)
      ? rawMessage
      : JSON.parse(rawMessage); 
  } catch (err) {
    throw new Error("rawMessage must be a valid array or JSON string.");
  }

  const errorContents = {};

  for (const item of messageList) {
    const loc = item?.loc;
    const msg = item?.msg;
    if (Array.isArray(loc) && loc.length > 0) {
      const errorKey = loc.join(".");
      errorContents[errorKey] = msg || "No error message provided.";
    }
  }

  if (Object.keys(errorContents).length === 0) {
    return `${errorMessageString}:\n\nNo specific validation errors provided.`;
  }
  const errorDetails = Object.entries(errorContents)
    .map(([key, value]) => `"${key}": "${value}"`)
    .join("\n\n");
  return `${errorMessageString}:\n\n${errorDetails}`;
};

export const fetchPdf = async (lambdaUrl) => {
  try {
    const response = await fetch(lambdaUrl);
    if (!response.ok) {
      throw new Error(`HTTP error status: ${response.status}`);
    }

    const pdfData = await response.arrayBuffer();
    return pdfData;
  } catch (error) {
    return null;
  }
};

export const validateForm = (name, phone, email) => {
  if (name == "" || phone == "" || email == "") {
    return {
      isValid: false,
      errorMessage: "Name, phone, and email are required.",
    };
  }

  if (
    typeof name !== "string" ||
    typeof phone !== "string" ||
    typeof email !== "string"
  ) {
    return {
      isValid: false,
      errorMessage: "All inputs must be strings.",
    };
  }

  if (!validateUserEmail(email)) {
    return {
      isValid: false,
      errorMessage: "The email format is invalid.",
    };
  }

  if (!validatePhoneNumber(phone)) {
    return {
      isValid: false,
      errorMessage:
        "Phone number is not valid. Please enter a valid number with a correct area code, including the country code (e.g. +1 for US).",
    };
  }

  return { isValid: true };
};

export const validateUserEmail = (email) => {
  if (!email) return false;

  const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,6}$/;
  return emailRegex.test(email);
};

export const validatePhoneNumber = (phone) => {
  const phoneNumber = parsePhoneNumberFromString(phone);
  return phoneNumber?.isValid() ?? false;
};

export   const formatPhoneNumber = (value) => {
    const digits = value.replace(/\D/g, "").slice(0, 10);
    const part1 = digits.slice(0, 3);
    const part2 = digits.slice(3, 6);
    const part3 = digits.slice(6, 10);
    let formatted = part1;
    if (part2) formatted += "-" + part2;
    if (part3) formatted += "-" + part3;
    return formatted;
  };